import { useState, useEffect, useMemo, useCallback } from 'react';
import { UpdateOrderBrandMeal, OrderType } from 'models/order.model';
import { OrderSingleMealEditType } from 'models/meal.model';

const transformOrderMeals = (order: OrderType): UpdateOrderBrandMeal[] => {
  return order.calculated_order.meals.map((orderMeal) => ({
    id: orderMeal.brand.id,
    brandName: orderMeal.brand.name,
    amount: orderMeal.amount,
    quantity: 1,
    meals: (orderMeal.meals ?? []).map((meal) => ({
      id: meal.id,
      name: meal.name,
      amount: Number(meal.amount),
      quantity: meal.quantity,
      order_note: meal.order_note,
      addons: (meal.addons ?? []).map((addon) => ({
        ...addon,
        name: addon.name ?? addon.meal_data.name,
        unit_amount:
          Number(addon.amount ?? addon.meal_data.amount) /
          (Number(addon.quantity) * Number(meal.quantity))
      }))
    }))
  }));
};

export const useOrderManagement = (order: OrderType | undefined) => {
  const [orderMeals, setOrderMeals] = useState<UpdateOrderBrandMeal[]>([]);
  const [initialOrderMeals, setInitialOrderMeals] = useState<UpdateOrderBrandMeal[]>([]);

  useEffect(() => {
    if (!order) return;
    const transformedMeals = transformOrderMeals(order);
    setOrderMeals(transformedMeals);
    setInitialOrderMeals(transformedMeals);
  }, [order]);

  const newTotal = useMemo(() => {
    return orderMeals.reduce((total, order) => {
      const orderTotal = order.meals.reduce((mealTotal, meal) => {
        const mealBaseAmount = Number(meal.amount) * Number(meal.quantity);
        const addonsTotal = meal.addons.reduce(
          (acc, addon) =>
            acc + Number(addon.unit_amount) * Number(addon.quantity) * Number(meal.quantity),
          0
        );
        return mealTotal + mealBaseAmount + addonsTotal;
      }, 0);
      return total + orderTotal;
    }, 0);
  }, [orderMeals]);

  const headerDetails = useMemo(() => {
    const brandsTotal = orderMeals.length;
    const mealsTotal = orderMeals.reduce((acc, order) => {
      return acc + order.meals.length;
    }, 0);

    return {
      brandsTotal,
      mealsTotal
    };
  }, [orderMeals]);

  const incrementPack = useCallback((meal: OrderSingleMealEditType) => {
    if (!meal) return;
    setOrderMeals((prev) =>
      prev.map((order) => ({
        ...order,
        meals: order.meals.map((m) =>
          m.id === meal.id ? { ...m, quantity: Number(m.quantity) + 1 } : m
        )
      }))
    );
  }, []);

  const decrementPack = useCallback((meal: OrderSingleMealEditType) => {
    if (!meal || Number(meal.quantity) < 2) return;

    setOrderMeals((prev) =>
      prev.map((order) => ({
        ...order,
        meals: order.meals.map((m) =>
          m.id === meal.id ? { ...m, quantity: Number(m.quantity) - 1 } : m
        )
      }))
    );
  }, []);
  const handleUndoChanges = useCallback(() => {
    setOrderMeals(initialOrderMeals);
  }, [initialOrderMeals]);

  const handleDeleteMeal = useCallback((mealId: string) => {
    setOrderMeals((prev) =>
      prev
        .map((order) => {
          return {
            ...order,
            meals: order.meals.filter((m) => m.id !== mealId)
          };
        })
        .filter((order) => order.meals.length > 0)
    );
  }, []);

  const hasOrderChanged = useMemo(() => {
    return JSON.stringify(orderMeals) !== JSON.stringify(initialOrderMeals);
  }, [orderMeals, initialOrderMeals]);

  return {
    orderMeals,
    decrementPack,
    incrementPack,
    handleDeleteMeal,
    headerDetails,
    setOrderMeals,
    handleUndoChanges,
    hasOrderChanged,
    newTotal
  };
};
